import { useQuery } from "@/lib/query";
import { Link, useParams } from "react-router";
import { ArrowLeft, CalendarClock, PanelRightOpen, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { BookingStatusBadge } from "@/components/common/status-badge";
import { CopilotPanel } from "@/components/doctor/copilot/copilot-panel";
import { PrescriptionReview } from "@/components/doctor/copilot/prescription-review";
import { RequireDoctor } from "@/components/doctor/require-doctor";
import { CallControls } from "@/components/doctor/video-call/call-controls";
import { CallStage } from "@/components/doctor/video-call/call-stage";
import { CallStatus } from "@/components/doctor/video-call/call-status";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useCopilot } from "@/hooks/doctor/use-copilot";
import { useTranscription } from "@/hooks/doctor/use-transcription";
import { useVideoCall } from "@/hooks/doctor/use-video-call";
import { useAudioCapture } from "@/hooks/doctor/use-audio-capture";
import { bookingsQuery, meQuery } from "@/lib/api/doctorApi";
import { isCallableBooking } from "@/lib/booking-view";
import { formatBookingSchedule } from "@/lib/format";
import { usePageMeta } from "@/lib/page-meta";
import { cn } from "@/lib/utils";
import { clearActiveCall, setActiveCall } from "@/services/video-call/active-call-store";

export function VideoCallRoute() {
  usePageMeta({
    title: "Video Consultation — HealNova Doctor Portal",
    description: "Run a secure HealNova video consultation with your patient from the browser.",
    robots: "noindex",
  });
  return <RequireDoctor>{() => <VideoCallPage />}</RequireDoctor>;
}

const LIVE_STATES = new Set([
  "waitingForPatient",
  "ongoing",
  "patientLeft",
  "reconnecting",
  "ending",
]);

function VideoCallPage() {
  const { bookingId = "" } = useParams();
  const { uid } = useAuth();
  const bookings = useQuery(bookingsQuery(uid));
  const doctor = useQuery(meQuery(uid));
  const booking = bookings.data?.find((item) => item.id === bookingId);
  const patientName = booking?.patientName ?? "The patient";

  const call = useVideoCall(bookingId);
  const live = LIVE_STATES.has(call.state);

  /*
   * The copilot is a companion to the call, not a participant. It is opened
   * alongside the call and torn down with the page; nothing it does is on the
   * path of starting, running or ending the consultation.
   */
  // `live` drives both automatic behaviours: transcription starts with the call,
  // and the copilot re-reads the transcript on its own until the call ends.
  const copilot = useCopilot(bookingId, { live });
  /*
   * `enabled: live` is the microphone's leash. The copilot panel stays mounted
   * after the call ends so the doctor can finish the prescription, and without
   * this the Transcribe button would still be there to open the mic on a call
   * that is over. Camera and microphone are in use during the consultation and
   * at no other time.
   */
  const transcription = useTranscription(copilot.session?.id ?? null, {
    enabled: live,
    onFlush: copilot.refreshTranscript,
  });

  // Doctor audio capture for AI transcript (integration spec)
  const audioCapture = useAudioCapture(call.localStream, live);
  const [panelOpen, setPanelOpen] = useState(true);

  const { open: openCopilot } = copilot;
  useEffect(() => {
    if (live) openCopilot();
  }, [live, openCopilot]);

  /**
   * Publishes the call to the shell's "Return to consultation" bar.
   *
   * Navigating away releases media but never ends the backend session, so the
   * patient stays in the room and the consultation keeps running. The record is
   * therefore written while the call is live and cleared only when it genuinely
   * is not — never on unmount, which is the exact moment the reminder starts
   * being useful.
   */
  useEffect(() => {
    if (live) {
      setActiveCall({ bookingId, patientName });
      return;
    }
    if (call.state === "ended" || call.state === "idle" || call.state === "error") {
      clearActiveCall(bookingId);
    }
  }, [bookingId, call.state, live, patientName]);

  /**
   * Stops the doctor's audio recording and uploads it, exactly once.
   *
   * Fires on `ending`, not `ended`: `use-video-call` sets `ending` synchronously
   * before it awaits `POST /call/end` and tears the Zego engine down, and that
   * teardown stops every track on the published stream. `MediaRecorder` has
   * nothing left to record once its tracks are stopped — waiting for `ended`
   * here would race the recorder against its own stream disappearing. This is
   * also independent of the copilot session: the transcript upload
   * (`POST /bookings/:id/ai-session/transcript`) is a separate, already-working
   * backend endpoint that owes nothing to whether the AI copilot panel's own
   * session (a different service) ever opened.
   */
  const audioFinalizedRef = useRef(false);
  useEffect(() => {
    if (call.state !== "ending" || audioFinalizedRef.current) return;
    audioFinalizedRef.current = true;
    transcription.stop();
    void audioCapture.stopAndUpload(bookingId);
  }, [call.state, transcription, audioCapture, bookingId]);

  /**
   * Kicks off end-of-call processing exactly once.
   *
   * Ordering is the whole point: `use-video-call` has already called
   * `POST /call/end`, already let the backend complete the booking, and already
   * invalidated the queries by the time `state` reads `ended`. Finalization runs
   * after all of that and returns 202 immediately, so it cannot delay or affect
   * any of it.
   */
  const finalizedRef = useRef(false);
  const { finalize } = copilot;
  useEffect(() => {
    if (call.state !== "ended" || finalizedRef.current) return;
    if (!copilot.session) return;
    finalizedRef.current = true;
    finalize();
  }, [call.state, copilot.session, finalize]);

  const showCopilot = copilot.enabled && (live || call.state === "ended");
  const showPrescription = call.state === "ended" && copilot.enabled;

  return (
    <div
      className={cn(
        "mx-auto w-full space-y-6",
        showCopilot && panelOpen ? "max-w-7xl" : "max-w-5xl",
      )}
    >
      <header className="space-y-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2 gap-2">
          <Link to="/bookings">
            <ArrowLeft className="size-4" aria-hidden /> Back to bookings
          </Link>
        </Button>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-rose uppercase">
              Video consultation
            </p>
            <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">
              {patientName}
            </h1>
            {booking && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <CalendarClock className="size-3.5" aria-hidden />
                {formatBookingSchedule(booking)}
              </p>
            )}
          </div>

          {showCopilot && !panelOpen && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() => setPanelOpen(true)}
            >
              <PanelRightOpen className="size-4" aria-hidden />
              Show AI copilot
            </Button>
          )}
        </div>
      </header>

      <div
        className={cn(
          "grid gap-5",
          showCopilot && panelOpen && "xl:grid-cols-[minmax(0,1fr)_22rem]",
        )}
      >
        <div className="min-w-0 space-y-5">
          {live ? (
            <>
              <CallStage
                state={call.state}
                patientName={patientName}
                hasRemoteStream={call.hasRemoteStream}
                remoteUserPresent={call.remoteUserPresent}
                cameraOff={call.cameraOff}
                setLocalView={call.setLocalView}
                remoteVideoRef={call.remoteVideoRef}
              />
              <CallControls
                state={call.state}
                micMuted={call.micMuted}
                cameraOff={call.cameraOff}
                onToggleMic={call.toggleMic}
                onToggleCamera={call.toggleCamera}
                onEndCall={call.endCall}
              />
            </>
          ) : booking && !isCallableBooking(booking) ? (
            // The backend rejects an initiate on a closed booking; say so up
            // front rather than letting the doctor press Start and collect a
            // 400. A missed booking is not closed — the doctor can still
            // connect a late patient — so it falls through to the normal flow.
            <div className="card-surface space-y-4 px-6 py-12 text-center">
              <div className="flex justify-center">
                <BookingStatusBadge status={booking.status} />
              </div>
              <p className="text-sm text-muted-foreground">
                {booking.status === "cancelled"
                  ? "This appointment was cancelled, so the video consultation can no longer be started."
                  : "This consultation is already marked completed."}
              </p>
              <Button asChild variant="outline" size="sm">
                <Link to="/bookings">Back to bookings</Link>
              </Button>
            </div>
          ) : (
            <CallStatus
              state={call.state}
              error={call.error}
              hasSession={Boolean(call.session)}
              endedDuration={call.endedDuration}
              patientAttended={call.patientAttended}
              resuming={call.resuming}
              onStart={call.startCall}
              onRetry={call.retry}
            />
          )}

          {/* §9–§12: the prescription flow opens once the call has ended. */}
          {showPrescription && (
            <PrescriptionReview
              copilot={copilot}
              doctorName={doctor.data?.name ?? "the attending doctor"}
            />
          )}
        </div>

        {showCopilot && panelOpen && (
          <CopilotPanel
            copilot={copilot}
            transcription={transcription}
            onCollapse={() => setPanelOpen(false)}
            className="xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)]"
          />
        )}
      </div>

      {/* Nothing else on this screen depends on the copilot, so when it is not
          configured the call reads exactly as it did before this feature. */}
      {!copilot.enabled && live && (
        <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Sparkles className="size-3.5" aria-hidden />
          The AI copilot is not configured for this environment.
        </p>
      )}
    </div>
  );
}
